import circleBg from '@/assets/notifications/resolution-none/medallion-circle-bg.svg';
import coinX from '@/assets/notifications/resolution-none/medallion-coin-x.png';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketDeepLinkId, marketTitle, metaString } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta, PopupLinkCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['resolution-none'];

/**
 * MARKET_RESOLUTION · NONE — nothing hit. Purple coin-with-X medallion, a
 * neutral (not red) "Market Resolved" message, and a "Find new markets" CTA. No
 * amount — nothing was added to the balance. Matches
 * `.arc111/figma/resolution-none/`.
 *
 * Figma quirk deliberately NOT reproduced: the design authored the first body
 * line with token `text/on-brand` (white → invisible on the white card). We
 * render every body line in `--ink` so the copy is actually visible.
 */
export function ResolutionNonePopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const eventName = metaString(meta, 'eventName') ?? marketTitle(meta);
  const secondary = CTA.secondary;
  const hasMarket = marketDeepLinkId(meta) !== undefined;

  return (
    <>
      <Medallion circleSrc={circleBg} illustrationSrc={coinX} tone="neutral" alt="" illustrationSize={190} />

      <div className="flex flex-col items-center gap-4 self-stretch px-6">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">Market Resolved</h2>

        {eventName ? (
          <p className="text-xl text-ink">
            <span className="font-bold">{`“${eventName}”`}</span> - your positions resolved the other way
          </p>
        ) : (
          <p className="text-xl text-ink">Your positions resolved the other way</p>
        )}

        <p className="text-xl text-ink">New markets are live now.</p>
      </div>

      <div className="flex flex-col items-center gap-3">
        <PopupCta
          label={CTA.primary.label}
          width={320}
          onClick={() => onAction(CTA.primary.action)}
          testId="popup-cta-primary"
        />
        {secondary && hasMarket && (
          <PopupLinkCta label={secondary.label} onClick={() => onAction(secondary.action)} testId="popup-cta-secondary" />
        )}
      </div>
    </>
  );
}
